import api from './api';
import type { Product } from './types';

const CACHE_MS = 5 * 60 * 1000;
let cached: Product[] | undefined;
let expiresAt = 0;
let pending: Promise<Product[]> | undefined;

const normalize = (value: string) => value.toLowerCase()
  .replace(/&amp;/g, ' ').replace(/[^a-z0-9]+/g, ' ').trim();

export function matchProducts(products: Product[], query: string, categoryId?: number, onSaleOnly = false): Product[] {
  const normalized = normalize(query);
  const terms = normalized.split(/\s+/).filter(Boolean);
  if (!terms.length) return [];
  return products.map((product, index) => {
    if (categoryId && !product.categories?.some(category => category.id === categoryId)) return null;
    if (onSaleOnly && !product.on_sale) return null;
    const title = normalize(product.name);
    const sku = normalize(product.sku || '');
    const categories = (product.categories || []).flatMap(category => [normalize(category.name), normalize(category.slug)]);
    const attributes = (product.attributes || [])
      .filter(attribute => /brand|manufacturer|model|sku/i.test(attribute.name))
      .flatMap(attribute => attribute.options.map(normalize));
    const words = [title, sku, ...categories, ...attributes].flatMap(field => field.split(/\s+/));
    if (!terms.every(term => words.some(word => word.startsWith(term)))) return null;
    const score = title === normalized || sku === normalized ? 4
      : categories.some(category => terms.every(term => category.split(/\s+/).some(word => word.startsWith(term)))) ? 3
      : title.startsWith(normalized) || sku.startsWith(normalized) ? 2 : 1;
    return { product, score, index };
  }).filter((item): item is { product: Product; score: number; index: number } => item !== null)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map(item => item.product);
}

async function loadCatalog(): Promise<Product[]> {
  if (cached && Date.now() < expiresAt) return cached;
  if (pending) return pending;
  pending = (async () => {
    const first = await api.get<Product[]>('/products', { params: { status: 'publish', per_page: 100, page: 1 } });
    const totalPages = Number(first.headers?.['x-wp-totalpages']);
    if (!Number.isInteger(totalPages) || totalPages < 1) {
      // Never silently search only the first page when pagination metadata is unavailable.
      if (first.data.length === 100) throw new Error('Unable to load the complete search catalog. Please try again.');
    }
    const products = [...first.data];
    for (let page = 2; page <= totalPages; page++) {
      const response = await api.get<Product[]>('/products', { params: { status: 'publish', per_page: 100, page } });
      products.push(...response.data);
    }
    cached = Array.from(new Map(products.map(product => [product.id, product])).values());
    expiresAt = Date.now() + CACHE_MS;
    return cached;
  })();
  try { return await pending; } finally { pending = undefined; }
}

export async function searchCatalog(query: string, page: number, perPage: number, categoryId?: number, onSaleOnly = false) {
  const matches = matchProducts(await loadCatalog(), query, categoryId, onSaleOnly);
  return {
    items: matches.slice((page - 1) * perPage, page * perPage),
    totalItems: matches.length,
    totalPages: Math.max(1, Math.ceil(matches.length / perPage)),
  };
}
